import { Component } from 'react';
import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { format, formatDistanceToNow } from 'date-fns';
import { marked } from 'marked';

import { Box } from '../../../shared/components';
import { useProjectEnvironments } from '../../hooks/useProjects';
import {
  type Account,
  type AuditLogEntry,
  type AuditLogEntryDetail,
  type AuditLogMember,
  type AuditLogParent,
} from '../../model';

import './styles.scss';

type EntryProps = Readonly<{
  entry: AuditLogEntry | AuditLogEntryDetail;
  account: Account;
  enableActorDisplay?: boolean;
}>;

const getProjectKey = (resource: string) => {
  const projectRegex = /(proj\/([a-zA-Z0-9-_.]+)):?/;
  const match = projectRegex.exec(resource);
  if (match) {
    return match[match.length - 1];
  }
  return undefined;
};

function ParentEnvironmentSwatch({ account, parent }: { account: Account; parent?: AuditLogParent }) {
  const projectKey = parent ? getProjectKey(parent.resource) : undefined;
  if (!parent || !projectKey) {
    return null;
  }
  return <EnvironmentColorSwatch account={account} projectKey={projectKey} environmentName={parent.name} />;
}

// Entries on a page come from any project, so each swatch looks its project's
// environments up itself; react-query shares one request per project.
function EnvironmentColorSwatch({
  account,
  projectKey,
  environmentName,
}: {
  account: Account;
  projectKey: string;
  environmentName: string;
}) {
  const { data } = useProjectEnvironments(account, projectKey);
  const environment = data?.items.find((candidate) => candidate.name === environmentName);
  if (!environment) {
    return null;
  }
  return <Icon icon={IconNames.SYMBOL_SQUARE} iconSize={22} color={`#${environment.color}`} />;
}

export class AuditLogEntryOverview extends Component<EntryProps> {
  public render() {
    const { entry, account, enableActorDisplay } = this.props;
    const actorName = enableActorDisplay ? this.getActorDisplay(entry) : this.getLegacyActorDisplay(entry);
    return (
      <>
        <Box className="AuditLogEntry-title">
          <span style={{ fontWeight: 'bold' }}>{actorName}</span>
          <span style={this.getTitleVerbTextColor(entry.titleVerb)}>
            {` ${entry.titleVerb}`}
            {entry.name === 'account' && ' the'}
          </span>
          <span style={{ fontWeight: 'bold' }}>{` ${entry.name}`}</span>
          {entry.parent && ` in ${entry.parent.name}`}
          <span>
            <ParentEnvironmentSwatch account={account} parent={entry.parent} />
          </span>
        </Box>
        <div className="AuditLogEntry-details">
          <TooltipTrigger>
            <Focusable>
              <span role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
                {formatDistanceToNow(entry.date, { addSuffix: true })}
              </span>
            </Focusable>
            <Tooltip placement="bottom" variant="popover">
              {format(new Date(entry.date), "EEEE, MMMM do, yyyy 'at' h:mm a")}
            </Tooltip>
          </TooltipTrigger>
          {entry.accesses[0].resource &&
            entry.accesses[0].resource.startsWith('proj') &&
            ` - ${getProjectKey(entry.accesses[0].resource)} project`}

          {entry.token && <Box>{` - using the token ${entry.token.name} (...${entry.token.ending})`}</Box>}
        </div>
        {entry.description && (
          <Box className="AuditLogEntry-description">
            <span
              // oxlint-disable-next-line react/no-danger -- rendering trusted, server-authored markdown for the audit log description; there is no safe fix that preserves markdown rendering here
              dangerouslySetInnerHTML={{
                __html: marked.parse(entry.description) as string,
              }}
            />
          </Box>
        )}
      </>
    );
  }

  private getLegacyActorDisplay = (entry: AuditLogEntry | AuditLogEntryDetail): string => {
    const { member } = entry;
    if (member) {
      return this.getMemberNameOrEmail(member);
    }
    return '';
  };

  private getActorDisplay = (entry: AuditLogEntry | AuditLogEntryDetail): string => {
    const { member, subject, app, token, kind } = entry;
    if (member) {
      return this.getMemberNameOrEmail(member);
    }
    if ((kind === 'member' || kind === 'team') && !token && !app && !subject?.name) {
      return 'SSO';
    }
    if (app?.isScim) {
      return 'SCIM';
    }
    if (token?.serviceToken) {
      return 'A service token';
    }
    if (app) {
      return app.name || 'An app';
    }
    if (token) {
      return `Token: ${token.name}`;
    }
    if (subject?.name && subject.name !== 'LaunchDarkly Admin') {
      return subject.name;
    }
    return 'LaunchDarkly';
  };

  private getMemberNameOrEmail = (member: AuditLogMember) => {
    if (member.firstName && member.lastName) {
      return `${member.firstName} ${member.lastName}`;
    }
    return member.email;
  };

  private getTitleVerbTextColor = (titleVerb: string) => {
    if (titleVerb.startsWith('created')) {
      return { color: 'green' };
    }
    if (titleVerb.startsWith('deleted')) {
      return { color: 'red' };
    }
    return { color: 'black' };
  };
}
